import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const pathQuestion = "/quiz/api/v1";
export const AnswerOptionModel = types.model({
  id: types.number,
  questionID: types.number,
  title: types.string,
  code: types.string,
  icon: types.maybeNull(types.string),
  displayOrder: types.number,
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const Section = types.model({
  id: types.number,
  title: types.string,
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const QuestionModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    complement: types.string,
    responseTypeId: types.maybeNull(types.string),
    isRequired: types.boolean,
    sectionID: types.maybeNull(types.number),
    relatedDataId: types.maybeNull(types.string),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        isRequired: String(self.isRequired),
      };
    },
  }));

export const QuestionDetailModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    complement: types.string,
    responseTypeId: types.maybeNull(types.string),
    isRequired: types.boolean,
    sectionID: types.maybeNull(types.number),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    relatedDataId: types.maybeNull(types.string),
    options: types.array(AnswerOptionModel),
    section: types.maybeNull(Section),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        isRequired: String(self.isRequired),
      };
    },
  }));

const QuestionnaireQuizStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathQuestion}/question`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: QuestionModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(QuestionDetailModel),
    isMoreLoading: types.optional(types.boolean, false),
    isQuestionLoading: types.optional(types.boolean, false),
    isQuestionManaging: types.optional(types.boolean, false),
    isQuestionPublishing: types.optional(types.boolean, false),
    isQuestionCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get questionOptions() {
      const { items } = getSnapshot(self);
      return items.map(
        ({
          id,
          title,
          code,
          complement,
          responseTypeId,
          isRequired,
          publishStatus,
        }) => ({
          label: title,
          value: {
            id,
            title,
            code,
            complement,
            responseTypeId,
            isRequired,
            publishStatus,
          },
        }),
      );
    },
  }))
  .actions(self => ({
    updateQuestion(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreQuestions: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `${pathQuestion}/question`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              search: self.search,
              orderBy,
              orderByDesc,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchQuestionDetails: flow(function*(id?: number) {
      self.isQuestionLoading = true;

      try {
        const details = yield api.get(
          `${pathQuestion}/question/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details: {
            ...details,
            responseTypeId: details.responseType,
            relatedDataId: details.relatedData,
          },
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuestionLoading = false;
      }
    }),

    createQuestion: flow(function*(payload: any) {
      self.isQuestionManaging = true;

      try {
        const { id } = yield api.post(
          `${pathQuestion}/question`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuestionManaging = false;
      }
    }),

    editQuestionInfo: flow(function*(payload: any) {
      self.isQuestionManaging = true;

      try {
        return yield api.put(
          `${pathQuestion}/question/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuestionManaging = false;
      }
    }),

    changeQuestionStatus: flow(function*(id: number, status: string) {
      self.isQuestionManaging = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathQuestion}/question/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateQuestion(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuestionManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default QuestionnaireQuizStore.create();
